import { EventApi, EventContentArg, MoreLinkArg } from '@fullcalendar/core';
import InteractionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import TimeGridPlugin from '@fullcalendar/timegrid';
import { CalendarTaskSync } from '@griply/types';
import { toZonedTime } from '@griply/utils';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { FullCalendarEvent } from 'shared/components/ui/full-calendar-event';
import { FullCalendarMorePopover } from 'shared/components/ui/full-calendar-more-popover';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useResizeObserver } from 'shared/hooks/use-resize-observer';
import { eventAllow } from 'shared/lib/full-calendar/event-allow';
import {
  fullCalendarLocaleMap,
  fullCalendarLocales,
} from 'shared/lib/full-calendar/locales';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import {
  fullCalendarMilitaryTime,
  fullCalendarStandardTime,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './full-calendar-day.style';

const plugins = [TimeGridPlugin, InteractionPlugin];

export type FullCalendarDayProps = Omit<
  React.ComponentProps<typeof FullCalendar>,
  | 'plugins'
  | 'initialView'
  | 'dayHeaderContent'
  | 'eventAllow'
  | 'contentHeight'
> & {
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onCompleteTask: (id: ID, date: Timestamp) => void;
  onClickEvent?: (id: ID) => void;
  referenceDate?: Timestamp;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  // external events are painted in this zone, so the now-indicator has to read it too
  timeZone: string;
  calendarSync?: CalendarTaskSync;
};

export const FullCalendarDay = forwardRef<HTMLDivElement, FullCalendarDayProps>(
  (
    {
      tasks,
      goals,
      lifeAreas,
      tags,
      smartLists,
      onCompleteTask,
      onClickEvent,
      referenceDate = new Date(),
      events,
      fullCalendarRef,
      timeFormat,
      weekStartsOn,
      timeZone,
      calendarSync,
      ...rest
    },
    ref,
  ) => {
    const locale = useI18nLocale();
    const _fullCalendarRef = useForwardedRef(fullCalendarRef);
    const scrollContainerRef = useForwardedRef<HTMLDivElement>(ref);
    const [itemWidth, setItemWidth] = useState(120);
    const [moreInfo, setMoreInfo] = useState<{
      position: { top: number; left: number };
      events: EventApi[];
    }>();

    // A function, not a Date: FullCalendar re-reads it as the indicator ticks.
    const getNow = useCallback(
      () => toZonedTime(new Date(), timeZone),
      [timeZone],
    );

    const onResize = useCallback(() => {
      _fullCalendarRef.current?.getApi().updateSize();
      setItemWidth((scrollContainerRef.current?.clientWidth ?? 7) / 7);
    }, [_fullCalendarRef, scrollContainerRef]);
    useResizeObserver(scrollContainerRef, onResize, 1);

    const onMore = useCallback(({ jsEvent, hiddenSegs }: MoreLinkArg) => {
      jsEvent.preventDefault();
      setMoreInfo({
        position: {
          top: (jsEvent as MouseEvent).clientY,
          left: (jsEvent as MouseEvent).clientX,
        },
        events: hiddenSegs.map(({ event }) => event),
      });

      return '';
    }, []);

    const closeMore = useCallback(() => setMoreInfo(undefined), []);

    const _onClickEvent = useCallback(
      (id: ID) => {
        onClickEvent?.(id);
        closeMore();
      },
      [closeMore, onClickEvent],
    );

    // Update the calendar view when referenceDate changes
    useEffect(() => {
      if (referenceDate && _fullCalendarRef.current) {
        queueMicrotask(() =>
          _fullCalendarRef.current?.getApi().gotoDate(referenceDate),
        );
      }
    }, [referenceDate, _fullCalendarRef]);

    const renderEvent = useCallback(
      ({ event }: EventContentArg) => (
        <FullCalendarEvent
          event={event}
          tasks={tasks}
          goals={goals}
          lifeAreas={lifeAreas}
          tags={tags}
          smartLists={smartLists}
          onComplete={onCompleteTask}
          onClick={_onClickEvent}
          calendarSync={calendarSync}
        />
      ),
      [
        tasks,
        goals,
        lifeAreas,
        tags,
        smartLists,
        onCompleteTask,
        _onClickEvent,
        calendarSync,
      ],
    );

    return (
      <Styled.ScrollContainer ref={scrollContainerRef}>
        <Styled.Container $itemWidth={itemWidth}>
          <FullCalendar
            // FullCalendar caches `now` at construction; only a fresh instance re-reads the zone.
            key={timeZone}
            ref={_fullCalendarRef}
            plugins={plugins}
            initialView="timeGridDay"
            slotDuration="00:15:00"
            slotLabelInterval="01:00:00"
            eventAllow={eventAllow}
            height="100%"
            locales={fullCalendarLocales}
            locale={fullCalendarLocaleMap[locale]}
            nowIndicator
            now={getNow}
            stickyHeaderDates
            eventResizableFromStart
            dayMaxEvents={4}
            moreLinkClick={onMore}
            eventContent={renderEvent}
            slotLabelFormat={
              timeFormat === TimeFormatOptions.Standard
                ? fullCalendarStandardTime
                : fullCalendarMilitaryTime
            }
            events={events}
            allDaySlot
            {...rest}
          />

          {moreInfo && (
            <FullCalendarMorePopover
              events={moreInfo.events}
              location={moreInfo.position}
              onOutside={closeMore}
              weekStartsOn={weekStartsOn}
              tasks={tasks}
              goals={goals}
              lifeAreas={lifeAreas}
              onComplete={onCompleteTask}
              tags={tags}
              smartLists={smartLists}
              onClick={_onClickEvent}
              calendarSync={calendarSync}
            />
          )}
        </Styled.Container>
      </Styled.ScrollContainer>
    );
  },
);
